import { History, PenLine, StickyNote } from "lucide-react";
import { useDataProvider, useNotify, type Identifier } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { AgentChip } from "../AgentChip";
import { useAgents } from "../useAgents";
import {
  historyBody,
  noteAgo,
  type ClientNote,
  type ContactNotes,
} from "./clientNotes";
import { NoteComposer } from "./NoteComposer";

/** How many notes show before "Ver N más". */
const VISIBLE_NOTES = 3;

interface ClientNotesProps {
  contactId: Identifier;
  notes: ContactNotes;
  /** Reload after writing, editing or deleting */
  onChanged: () => void;
  /** Opens the composer straight away */
  startWriting?: boolean;
  className?: string;
}

/**
 * Team notes of a client as sticky notes, newest first, with a box to write
 * one. Anyone can write; each person edits or deletes their own.
 */
export const ClientNotes = ({
  contactId,
  notes,
  onChanged,
  startWriting = false,
  className,
}: ClientNotesProps) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { meId } = useAgents();
  const [writing, setWriting] = useState(startWriting);
  const [showAll, setShowAll] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const run = async (action: () => Promise<unknown>, done: string) => {
    try {
      await action();
      notify(done, { type: "success" });
      onChanged();
      return true;
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "No se ha podido guardar",
        { type: "error" },
      );
      return false;
    }
  };

  const create = (text: string) =>
    run(
      () =>
        dataProvider.create("contact_notes", {
          data: {
            contact_id: contactId,
            text,
            date: new Date().toISOString(),
            sales_id: meId ?? null,
          },
        }),
      "Nota guardada",
    ).then((ok) => {
      if (ok) setWriting(false);
      return ok;
    });

  const update = (note: ClientNote, text: string) =>
    run(
      () =>
        dataProvider.update("contact_notes", {
          id: note.id,
          data: { text },
          previousData: note,
        }),
      "Nota cambiada",
    ).then((ok) => {
      if (ok) setEditingId(null);
      return ok;
    });

  const remove = (note: ClientNote) =>
    run(
      () =>
        dataProvider.delete("contact_notes", {
          id: note.id,
          previousData: note,
        }),
      "Nota borrada",
    );

  const team = notes.team;
  const visible = showAll ? team : team.slice(0, VISIBLE_NOTES);
  const hidden = team.length - visible.length;

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
          <StickyNote className="h-3.5 w-3.5" />
          Notas del equipo{team.length ? ` · ${team.length}` : ""}
        </span>
        {!writing && team.length ? (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 rounded-full px-2.5 text-amber-900 hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-400/15"
            onClick={() => setWriting(true)}
          >
            <PenLine className="h-3.5 w-3.5" />
            Escribir nota
          </Button>
        ) : null}
      </div>

      {writing ? (
        <NoteComposer onSave={create} onCancel={() => setWriting(false)} />
      ) : null}

      {!writing && !team.length ? (
        <button
          type="button"
          onClick={() => setWriting(true)}
          className="flex items-center gap-2 rounded-xl border border-dashed border-amber-300 bg-amber-50/60 px-3 py-2.5 text-left text-sm text-amber-900 transition-colors hover:bg-amber-100 dark:border-amber-500/40 dark:bg-amber-400/10 dark:text-amber-200 dark:hover:bg-amber-400/15"
        >
          <PenLine className="h-4 w-4 shrink-0" />
          <span>
            <strong className="font-medium">Escribir nota</strong>
            <span className="text-amber-800/70 dark:text-amber-200/60">
              {" "}
              · lo que te ha dicho, cuándo volver a llamar, qué le preocupa…
            </span>
          </span>
        </button>
      ) : null}

      {visible.map((note) =>
        editingId === String(note.id) ? (
          <NoteComposer
            key={note.id}
            initialText={note.text ?? ""}
            onSave={(text) => update(note, text)}
            onCancel={() => setEditingId(null)}
          />
        ) : (
          <NoteCard
            key={note.id}
            note={note}
            isMine={meId != null && String(note.sales_id) === String(meId)}
            onEdit={() => setEditingId(String(note.id))}
            onDelete={() => remove(note)}
          />
        ),
      )}

      {hidden > 0 || (showAll && team.length > VISIBLE_NOTES) ? (
        <button
          type="button"
          onClick={() => setShowAll(!showAll)}
          className="w-fit text-xs font-medium text-amber-900 hover:underline"
        >
          {showAll
            ? "Ver menos"
            : `Ver ${hidden} nota${hidden === 1 ? "" : "s"} más`}
        </button>
      ) : null}

      {notes.history.map((note) => (
        <HistoryNote key={note.id} note={note} />
      ))}
    </div>
  );
};

/** One note, readable at a glance: big text, who and when underneath. */
const NoteCard = ({
  note,
  isMine,
  onEdit,
  onDelete,
}: {
  note: ClientNote;
  isMine: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) => {
  const { nameOf } = useAgents();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const author = nameOf(note.sales_id);
  return (
    <div className="group flex flex-col gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-3 shadow-[0_1px_0_rgba(180,140,0,0.15)] dark:border-amber-500/30 dark:bg-amber-400/10 dark:shadow-none">
      <p className="whitespace-pre-line break-words text-[15px] leading-snug text-neutral-900 dark:text-amber-50">
        {note.text}
      </p>
      <div className="flex items-center gap-2 text-xs text-amber-900/70 dark:text-amber-200/70">
        {note.sales_id != null && author ? (
          <AgentChip id={note.sales_id} name={author.split(" ")[0]} />
        ) : null}
        <span title={new Date(note.date).toLocaleString("es-ES")}>
          {noteAgo(note.date)}
        </span>
        {isMine ? (
          <span className="ml-auto flex items-center gap-3 opacity-70 transition-opacity group-hover:opacity-100">
            {confirmDelete ? (
              <>
                <button
                  type="button"
                  onClick={onDelete}
                  className="font-medium text-red-700 hover:underline dark:text-red-400"
                >
                  Sí, borrar
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  className="hover:underline"
                >
                  No
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onEdit}
                  className="hover:underline"
                >
                  Editar
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="hover:underline"
                >
                  Borrar
                </button>
              </>
            )}
          </span>
        ) : null}
      </div>
    </div>
  );
};

/** The Excel follow-up from before the CRM, folded to two lines. */
const HistoryNote = ({ note }: { note: ClientNote }) => {
  const [open, setOpen] = useState(false);
  return (
    <button
      type="button"
      onClick={() => setOpen(!open)}
      className="flex flex-col gap-1 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-left text-xs text-neutral-600 hover:bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800"
      title={open ? "Plegar" : "Ver todo"}
    >
      <span className="flex items-center gap-1.5 font-medium text-neutral-500 dark:text-neutral-400">
        <History className="h-3.5 w-3.5" />
        Seguimiento del Excel (antes del CRM)
      </span>
      <span
        className={cn(
          "whitespace-pre-line break-words",
          open ? "" : "line-clamp-2",
        )}
      >
        {historyBody(note.text)}
      </span>
    </button>
  );
};
